import React, { useState, useMemo, useCallback } from 'react';
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  ReferenceLine 
} from 'recharts';
import { 
  Card, 
  CardContent, 
  Box, 
  TextField, 
  MenuItem, 
  Switch, 
  FormControlLabel,
  Button,
  Slider,
  Typography
} from '@mui/material';
import _ from 'lodash';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#a4de6c', '#d0ed57', '#8dd1e1'];

/**
 * Flatten out the data from fundDataMap (i.e. the already filtered dictionary).
 * `fundDataMap` is presumably an object keyed by fundName, with an array of row objects.
 * Each row typically has: date, fund, status, marketValueBase, etc.
 * We'll unify them to a single array for charting.
 */
const unifyChartDataFromFundDataMap = (fundDataMap) => {
  // fundDataMap = { [fundName]: [ { date, fund, status, marketValueBase, ... }, ... ] }
  const allRows = Object.values(fundDataMap).flat();
  return allRows;
};

// Just a small helper to convert date strings to YYYY-MM-DD
const normalizeDate = (rawDate) => {
  // If `rawDate` is a string like "2024-03-31", it’s already okay,
  // but let's convert to "YYYY-MM-DD" for consistency.
  return new Date(rawDate).toISOString().slice(0, 10);
};

const getQuartersDifference = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const yearsDiff = d2.getFullYear() - d1.getFullYear();
  const quartersDiff = Math.floor(d2.getMonth() / 3) - Math.floor(d1.getMonth() / 3);
  return yearsDiff * 4 + quartersDiff;
};

/**
 * This is optional if you want to incorporate an overlay for the entire portfolio's market value
 * by date using a growth rate. If not needed, you could skip this logic.
 */
const calculatePortfolioValueAtDate = (baseValue, growthRate, asOfDate, targetDate) => {
  let gRate = growthRate > 0.5 ? growthRate / 100 : growthRate; // interpret as % if > 0.5
  if (gRate < 0) gRate = 0;

  const quartersDiff = getQuartersDifference(asOfDate, targetDate);
  const quarterlyRate = Math.pow(1 + gRate, 0.25) - 1; 
  return baseValue * Math.pow(1 + quarterlyRate, quartersDiff);
};

/**
 * Takes the flattened array of pacing rows (each row has a date, marketValueBase, fund, status, etc.),
 * groups them by date & groupBy dimension, sums `marketValueBase`, and optionally calculates % vs portfolioValueAtDate.
 */
const processChartData = (flattenedRows, groupBy, portfolioInfo, showPercentage) => {
  if (!flattenedRows?.length) return [];

  const baseValue = parseFloat(portfolioInfo?.portfolioMarketValue ?? 0) || 0;
  let growthRate = parseFloat(portfolioInfo?.growthRate ?? 0) || 0;
  growthRate = growthRate > 0.5 ? growthRate / 100 : growthRate;
  if (growthRate < 0) growthRate = 0;

  const asOfDate = portfolioInfo?.asOfDate || '';

  // Convert each row's date to YYYY-MM-DD for grouping
  const rowObjects = flattenedRows.map(r => ({
    ...r,
    dateStr: normalizeDate(r.date),
  }));

  // Group by date first
  const groupedByDate = _.groupBy(rowObjects, 'dateStr');
  const chartRows = [];

  for (const dateStr of Object.keys(groupedByDate).sort()) {
    const items = groupedByDate[dateStr];
    const itemsByGroup = _.groupBy(items, groupBy);

    // If needed, overlay "portfolioValueAtDate"
    const portfolioValueAtDate = calculatePortfolioValueAtDate(baseValue, growthRate, asOfDate, dateStr);

    const rowObj = { date: dateStr, portfolioValueAtDate };

    for (const groupVal of Object.keys(itemsByGroup)) {
      // Sum the marketValueBase for that group
      const sumMarketValueBase = _.sumBy(itemsByGroup[groupVal], 'marketValueBase');
      // If showPercentage, convert to (sum / portfolioValueAtDate) * 100
      rowObj[groupVal] = (showPercentage && portfolioValueAtDate)
        ? (sumMarketValueBase / portfolioValueAtDate) * 100
        : sumMarketValueBase;
    }
    chartRows.push(rowObj);
  }

  return chartRows;
};

const PacingInteractiveGraph = ({
  fundDataMap,
  fundInformation,
  pacingData,
  portfolioInfo,
  asOfDate  // optional if you want to keep that red reference line
}) => {
  const [groupBy, setGroupBy] = useState('fundStatus');
  const [targetPercent, setTargetPercent] = useState(10);
  const [showPercentage, setShowPercentage] = useState(false);
  const [yearRange, setYearRange] = useState([2020, 2035]); // default slider range

  // 1) Flatten the data from fundDataMap (the filtered dictionary).
  const flattenedRows = useMemo(() => {
    // If needed, you can combine fundDataMap with raw pacingData or fundInformation. 
    // But presumably, `fundDataMap` is already the parent’s filtered data. 
    return unifyChartDataFromFundDataMap(fundDataMap);
  }, [fundDataMap]);

  // 2) Process chart data
  const chartData = useMemo(() => {
    if (!flattenedRows?.length) return [];

    // Build the aggregated data by date and group dimension
    const processed = processChartData(flattenedRows, groupBy, portfolioInfo, showPercentage);

    // Add a "target" line
    const finalData = processed.map(item => ({
      ...item,
      target: showPercentage
        ? targetPercent  // direct percentage
        : (item.portfolioValueAtDate ?? 0) * (targetPercent / 100),
    }));

    // Filter by selected year range from the slider
    return finalData.filter(row => {
      const rowYear = new Date(row.date).getFullYear();
      return rowYear >= yearRange[0] && rowYear <= yearRange[1];
    });
  }, [flattenedRows, groupBy, portfolioInfo, showPercentage, targetPercent, yearRange]);

  // 3) Unique groups to render stacked bars
  const uniqueGroups = useMemo(() => {
    if (!chartData.length) return [];
    const keys = new Set();
    for (let row of chartData) {
      Object.keys(row).forEach(k => {
        if (!['date', 'portfolioValueAtDate', 'target'].includes(k)) {
          keys.add(k);
        }
      });
    }
    return Array.from(keys);
  }, [chartData]);

  // Format date for X-axis / tooltip
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const dateObj = new Date(dateStr);
    return `Q${Math.floor(dateObj.getMonth() / 3) + 1} ${dateObj.getFullYear()}`;
  };

  // Format values as either % or millions
  const formatValue = (val) => {
    if (val == null) return '';
    return showPercentage
      ? `${val.toFixed(2)}%`
      : `${(val / 1_000_000).toFixed(1)}M`;
  };

  // Year range slider handler
  const handleSliderChange = (_, newValue) => {
    setYearRange(newValue);
  };

  return (
    <Card sx={{
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
      },
    }}>
      <CardContent>

        {/* Controls */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              select
              label="Group By"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="assetClass">Asset Class</MenuItem>
              <MenuItem value="strategy">Strategy</MenuItem>
              <MenuItem value="fundStatus">Fund Status</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="fund">Fund</MenuItem>
              <MenuItem value="vintage">Vintage</MenuItem>
              <MenuItem value="localCurrency">Fund Currency</MenuItem>
            </TextField>

            <TextField
              label="Target Percent (%)"
              type="number"
              value={targetPercent}
              onChange={(e) => setTargetPercent(Number(e.target.value))}
              sx={{ width: 160 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showPercentage}
                  onChange={() => setShowPercentage(prev => !prev)}
                />
              }
              label={showPercentage ? '%' : '$'}
            />
            {/* <Button variant="contained" onClick={downloadData}>
              Download Data
            </Button> */}
          </Box>
        </Box>

        {/* Slider for year range */}
        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom>Year Range</Typography>
          <Slider
            value={yearRange}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            min={2000}
            max={2050}
            step={1}
            marks={[
              { value: 2020, label: '2020' },
              { value: 2035, label: '2035' }
            ]}
          />
        </Box>

        {/* Chart */}
        <div style={{ height: 500, width: '100%' }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDate} />
              <YAxis
                domain={[0, dataMax => Math.max(dataMax * 1.1, 1)]}                tickFormatter={formatValue}
                label={{
                  value: showPercentage ? 'Percentage (%)' : 'Millions ($)',
                  angle: -90,
                  position: 'insideLeft'
                }}
              />
              <Tooltip labelFormatter={formatDate} formatter={formatValue} />
              <Legend />

              {uniqueGroups.map((group, index) => (
                <Bar
                  key={group}
                  dataKey={group}
                  stackId="a"
                  fill={COLORS[index % COLORS.length]}
                  name={group}
                />
              ))}

              <Line
                type="monotone"
                dataKey="target"
                stroke="#ff7300"
                strokeWidth={2}
                dot={false}
                name="Target"
              />

              {asOfDate && (
                <ReferenceLine
                  x={asOfDate}
                  stroke="red"
                  strokeDasharray="3 3"
                  label={{ value: 'As-of Date', position: 'top', fill: 'red' }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(PacingInteractiveGraph);